import { API_V2 } from '@front-app/services/api';
import { ImageUrl } from '@front-app/services/ImageApi/ImageApi';
import { ImageScale } from '@front-app/services/ImageApi/ImageModel';

export function blobToDataUrl(blob: Blob): Promise<ImageUrl> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(blob);
		reader.onload = () => {
			if (reader.result === null) {
				reject(new Error('Unable to read image data'));
			} else {
				resolve(reader.result as ImageUrl);
			}
		};
	});
}

export type GetImage = (id: string, headers: Record<string, string>, params?: ImageParamsV2) => Promise<ImageUrl>;

export interface ImageParamsV2 {
	scale?: ImageScale;
}

export function imageFetcher(urlProvider: (id: string) => string): GetImage {
	return (id: string, headers: Record<string, string>, params?: ImageParamsV2): Promise<ImageUrl> => API_V2
		.get(urlProvider(id), {
			responseType: 'blob',
			params,
			headers,
			timeout: 0,
		})
		.then(({ data }) => data as Blob)
		.then(blobToDataUrl);
}

export interface ImageUrlAndMimeType {
	imageUrl: string;
	mimeType: string;
}

export async function readImageFileAsUrlWithMimeType(file: File): Promise<ImageUrlAndMimeType> {
	const reader = new FileReader();
	const imageUrl = URL.createObjectURL(file);
	return new Promise((resolve, reject) => {
		reader.onload = (e) => {
			const result = e.target?.result;
			if (!result || typeof result === 'string') {
				reject(new Error('Could not read image'));
				return;
			}
			const mimeType = getMimeType(result, file.type);
			resolve({
				imageUrl,
				mimeType,
			});
		};
		reader.readAsArrayBuffer(file);
	});
}

// from: https://norserium.github.io/vue-advanced-cropper/guides/recipes.html#load-image-from-a-disc
export function getMimeType(file: ArrayBufferLike, fallback: string): string {
	const byteArray = (new Uint8Array(file)).subarray(0, 4);
	let header = '';
	for (let i = 0; i < byteArray.length; i++) {
		header += byteArray[i].toString(16);
	}
	switch (header) {
	case '89504e47':
		return 'image/png';
	case '47494638':
		return 'image/gif';
	case 'ffd8ffe0':
	case 'ffd8ffe1':
	case 'ffd8ffe2':
	case 'ffd8ffe3':
	case 'ffd8ffe8':
		return 'image/jpeg';
	default:
		return fallback;
	}
}

export const blobToFile = (theBlob: Blob, fileName: string): File => new File([theBlob], fileName, { lastModified: new Date().getTime(), type: theBlob.type });
